/* =============================================================================
 *  Kreux Matrix — Legal · /imprint
 *  Dedicated route · opens in a new tab from the Terminal footer.
 *  Mirrors the platform's luxury obsidian / glassmorphic aesthetic.
 *
 *  Brand vars only · Zero hardcoded brand-palette hex · Zero state.
 * ===========================================================================*/

import { useLang } from "@/lib/LangContext";
import LegalLangToggle from "@/components/LegalLangToggle";

const dict = {
  en: {
    Title: "Imprint",
    Back: "Back to Kreux Matrix",
    Sections: [
      {
        heading: "Information provided according to Sec. 5 Digitale-Dienste-Gesetz (DDG)",
        body: [
          "HANS . D . VAGHASIYA",
          "COLINI STRAßE 5",
          "68161 Mannheim-Oststadt",
          "Germany"
        ]
      },
      {
        heading: "Contact",
        body: [
          "Email: hans@kreuxmatrix.de",
          "Phone: +49 17685984847"
        ]
      },
      {
        heading: "EU Dispute Resolution",
        body: [
          "The European Commission provides a platform for online dispute resolution (OS): https://ec.europa.eu/consumers/odr/. Please find our email in the impressum/legal notice."
        ]
      },
      {
        heading: "Consumer Dispute Resolution / Universal Arbitration Board",
        body: [
          "We are not willing or obliged to participate in dispute resolution proceedings in front of a consumer arbitration board."
        ]
      },
      {
        heading: "Liability for Content & Links",
        body: [
          "As service providers, we are liable for own contents of these websites according to Sec. 7, paragraph 1 DDG. However, according to Sec. 8 to 10 DDG, we are not obligated to permanently monitor submitted or stored information. We have no influence on external third-party links and cannot guarantee their contents."
        ]
      },
      {
        heading: "Copyright",
        body: [
          "Contents and compilations published on these websites are subject to German copyright laws. Reproduction, editing, and distribution require written permission of the author."
        ]
      }
    ]
  },
  de: {
    Title: "Impressum",
    Back: "Zurück zu Kreux Matrix",
    Sections: [
      {
        heading: "Angaben gemäß § 5 DDG",
        body: [
          "HANS . D . VAGHASIYA",
          "COLINI STRAßE 5",
          "68161 Mannheim-Oststadt",
          "Deutschland"
        ]
      },
      {
        heading: "Kontakt",
        body: [
          "E-Mail: hans@kreuxmatrix.de",
          "Telefon: +49 17685984847"
        ]
      },
      {
        heading: "EU-Streitschlichtung",
        body: [
          "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/. Unsere E-Mail-Adresse finden Sie oben im Impressum."
        ]
      },
      {
        heading: "Verbraucherstreitbeilegung / Universalschlichtungsstelle",
        body: [
          "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen."
        ]
      },
      {
        heading: "Haftung für Inhalte & Links",
        body: [
          "Als Diensteanbieter haften wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Webseiten. Gemäß §§ 8 bis 10 DDG sind wir jedoch nicht verpflichtet, übermittelte oder gespeicherte Informationen permanent zu überwachen. Wir haben keinen Einfluss auf externe Links und können deren Inhalte nicht garantieren."
        ]
      },
      {
        heading: "Urheberrecht",
        body: [
          "Die auf diesen Webseiten veröffentlichten Inhalte und Zusammenstellungen unterliegen dem deutschen Urheberrecht. Vervielfältigung, Bearbeitung und Verbreitung bedürfen der schriftlichen Genehmigung des Autors."
        ]
      }
    ]
  }
};

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ff176637-490c-4d8d-8821-0039665e7b20/artifacts/1ppcjvci_Web%20logo.png";

const HexMatrix = () => (
  <svg
    aria-hidden="true"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute inset-0"
  >
    <defs>
      <pattern id="kx-hex-imp" x="0" y="0" width="56" height="98" patternUnits="userSpaceOnUse">
        <path
          d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M0 48 L0 80 L28 96 L56 80 L56 48"
          fill="none"
          stroke="rgba(255,255,255,0.04)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#kx-hex-imp)" />
  </svg>
);

const linkifyText = (text) => {
  /* Replace any http(s) URL with an anchor that opens in a new tab,
   * styled to match the cyan brand var. */
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[var(--il-cyan)] underline decoration-[var(--il-cyan)]/40 underline-offset-4 hover:decoration-[var(--il-cyan)] transition-colors duration-300 break-all"
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

const ImprintPage = () => {
  const { lang } = useLang();
  const t = dict[lang] || dict.en;

  return (
    <main
      data-testid="imprint-page"
      className="relative w-full min-h-screen font-sans overflow-x-hidden"
      style={{
        "--il-white": "#FFFFFF",
        "--il-cyan":  "#00E5FF",
        "--il-steel": "#1B3245",
        "--il-navy":  "#112332",
        "--il-amber": "#FFB300",
        background:
          "linear-gradient(180deg, #112332 0%, #0A1520 60%, #050B12 100%)",
        color: "#FFFFFF",
      }}
    >
      {/* Background hex matrix */}
      <div className="absolute inset-0 z-0 opacity-25 pointer-events-none animate-pulse" style={{ animationDuration: "8s" }}>
        <HexMatrix />
      </div>

      {/* In-page EN/DE toggle for direct-link visitors */}
      <LegalLangToggle />

      {/* Ambient cyan radial glow — subtle */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 40% at 50% 20%, rgba(0,229,255,0.08) 0%, transparent 70%)",
        }}
      />

      {/* Top nav strip — logo lockup + back link */}
      <nav
        data-testid="imprint-nav"
        className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 py-8 flex items-center justify-between"
      >
        <a
          href="/"
          className="flex items-center gap-3 no-underline group"
          data-testid="imprint-brand"
        >
          <img
            src={LOGO_URL}
            alt="Kreux Matrix"
            className="w-8 h-8 object-contain select-none"
            draggable="false"
          />
          <span className="text-xl md:text-2xl font-light tracking-tighter text-white uppercase">
            Kreux Matrix
          </span>
        </a>
        <a
          href="/"
          data-testid="imprint-back"
          className="text-xs font-mono text-white/50 hover:text-[var(--il-cyan)] tracking-[0.2em] uppercase transition-colors duration-300 no-underline"
        >
          ← {t.Back}
        </a>
      </nav>

      {/* Content card */}
      <section className="relative z-10 w-full max-w-4xl mx-auto px-6 lg:px-12 pb-24 pt-8">
        <div
          data-testid="imprint-card"
          className="relative rounded-3xl bg-white/5 backdrop-blur-3xl border border-[var(--il-cyan)]/20 shadow-[0_0_80px_-20px_var(--il-cyan)] p-10 md:p-16 overflow-hidden"
        >
          {/* Inner cyan top-edge highlight */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(0,229,255,0.6), transparent)",
            }}
          />

          {/* Eyebrow */}
          <span className="block font-mono text-[10px] tracking-[0.32em] uppercase text-[var(--il-cyan)]/80 mb-4">
            § 5 DDG · Legal Notice
          </span>

          {/* Title — metallic etched */}
          <h1
            data-testid="imprint-title"
            className="text-5xl md:text-7xl font-light tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white/80 to-white/20 drop-shadow-[0_0_30px_rgba(255,255,255,0.1)] leading-tight pb-2 mb-10 m-0"
          >
            {t.Title}
          </h1>

          {/* Divider hairline */}
          <span
            aria-hidden="true"
            className="block w-16 h-px mb-12"
            style={{
              background: "linear-gradient(90deg, var(--il-cyan), transparent)",
              boxShadow: "0 0 12px var(--il-cyan)",
            }}
          />

          {/* Sections */}
          <div className="flex flex-col gap-10" data-testid="imprint-sections">
            {t.Sections.map((s, i) => (
              <article key={i} data-testid={`imprint-section-${i}`}>
                <h2 className="text-xs font-mono tracking-[0.22em] uppercase text-[var(--il-cyan)] mb-4">
                  {s.heading}
                </h2>
                <div className="flex flex-col gap-2">
                  {s.body.map((line, j) => (
                    <p
                      key={j}
                      className="text-base md:text-lg text-white/70 font-light leading-relaxed m-0 break-words"
                    >
                      {linkifyText(line)}
                    </p>
                 ))}
              </div>
            </article>
          ))}

          {/* Close-Tab CTA */}
          <div className="mt-16 pt-10 border-t border-white/10 flex items-center justify-center">
            <button
              type="button"
              data-testid="pp-close-tab"
              onClick={() => window.close()}
              className="relative group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] transition-colors"
            >
              <span aria-hidden="true" className="w-1.5 h-1.5 rotate-45 bg-[var(--il-cyan)] shadow-[0_0_10px_var(--il-cyan)] group-hover:scale-125 transition-transform"></span>
              <span>{t.Title === "Imprint" ? "CLOSE TAB" : "TAB SCHLIESSEN"}</span>
            </button>
          </div>
    </div>
  </div>
</section>

      {/* Footer mini-bar */}
      <div className="mt-10 text-center">
        <span className="text-[10px] font-mono text-white/30 tracking-widest uppercase">
          © 2026 Kreux Matrix · All rights reserved
        </span>
      </div>
    </main>
  );
};

export default ImprintPage;
